/**
 * ENT-1958: preserve a signed-out deep link through the `/auth` (+ MFA)
 * round trip.
 *
 * Route guards (AdminLayout, VendorLayout, AppLayout) redirect an
 * unauthenticated caller to `/auth`. Historically they dropped the
 * original URL, so a cold click on an admin/vendor/dashboard deep link
 * (e.g. a Slack marketplace-listing notification) landed on the surface
 * overview after sign-in instead of the intended destination.
 *
 * This builds the `/auth` redirect path with the caller's full current
 * URL (path + query + hash) encoded as a `returnTo` param. `Auth.tsx`
 * already consumes `returnTo` after a successful sign-in (and the MFA
 * gate renders inside `/auth`, so the param survives the TOTP hop), and
 * validates it against `ENROLMENT_UNAWARE_RETURN_PREFIXES` before
 * navigating — so this only needs to faithfully capture and encode the
 * origin URL.
 */
type ReturnToLocation = {
  pathname: string;
  search?: string;
  hash?: string;
};

export function buildAuthRedirectPath(location: ReturnToLocation): string {
  const returnTo = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;

  // Nothing worth preserving: the bare landing page, or the auth route
  // itself (guarding against a self-referential `returnTo=/auth` loop).
  if (!returnTo || returnTo === '/' || returnTo.startsWith('/auth')) {
    return '/auth';
  }

  return `/auth?returnTo=${encodeURIComponent(returnTo)}`;
}

// ENT-1253 / codex round 2 P1: Auth.tsx opts into `allowMfaPendingSession`
// on the controller, which means a factor-less user in an `mfa_required`
// context can complete an AAL1 sign-in and land on the destination route.
// That is safe only if the destination is an MFA-enforcement-aware layout
// (AppLayout / AdminLayout / VendorLayout). A `?returnTo=` deep link can
// point anywhere that starts with `/`, so we must deny return paths that
// route to surfaces outside those layouts — `/vendor/claim*` is the
// known case (outside VendorLayout, runs claimProfile writes). Any
// denied returnTo falls through to the standard dashboard-route
// resolution below, which lands in AppLayout / AdminLayout / VendorLayout
// where the enrolment screen renders for AAL1 users.
const ENROLMENT_UNAWARE_RETURN_PREFIXES = [
  '/vendor/claim',
];

export function isEnrolmentAwareReturnRoute(returnTo: string): boolean {
  // Strip any query/hash so a path like `/vendor/claim?domain=x` still
  // matches the prefix. The original raw returnTo is what gets navigated;
  // this is purely a safety classification.
  const path = returnTo.split(/[?#]/, 1)[0];
  return !ENROLMENT_UNAWARE_RETURN_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`),
  );
}

/** Validate a decoded returnTo into a safe same-origin app path, or null. See ticket truth table. */
export function safeReturnToPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw === '/' || raw === '/auth' || raw.startsWith('/auth?') || raw.startsWith('/auth/') || raw.startsWith('/auth#')) return null;
  if (!isEnrolmentAwareReturnRoute(raw)) return null;
  return raw;
}
